/** @format */

import React from "react";
import {
    Box,
    Table,
    TableBody,
    TableHead,
    TableRow,
    Chip,
    Typography,
} from "@mui/material";

import {
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
} from "../styles";
import {
    IPredictionAnalysis,
    PredictionRiskLevel,
    PREDICTION_RISK_LEVEL_LABELS,
} from "../../../utility/types/collectTypes";

// ─────────────────────────────────────────────────────────────────────────
// SavedScenarios (Stage 2J) — read-only list of the what-if scenarios stored
// against a prediction. The baseline is never one of these (AC6).
// ─────────────────────────────────────────────────────────────────────────

interface Props {
    prediction: IPredictionAnalysis;
}

const FONT = "'Montserrat', sans-serif";

const RISK_COLORS: Record<PredictionRiskLevel, string> = {
    low: "#16a34a",
    medium: "#f59e0b",
    high: "#ea580c",
    critical: "#dc2626",
};

function fmtCurrency(amount: number, currency = "USD"): string {
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `$${Math.round(amount)}`;
    }
}

function fmtDate(value?: string): string {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    } catch {
        return "—";
    }
}

const SavedScenarios: React.FC<Props> = ({ prediction }) => {
    const scenarios = prediction.scenarios || [];
    const currency =
        (prediction.chartData?.find((c) => c.type === "receivables_timeline")?.data
            ?.currency as string) || "USD";

    return (
        <CollectSectionCard>
            <CollectSectionHeader>
                <Box>
                    <CollectSectionTitle>Saved scenarios</CollectSectionTitle>
                    <CollectSectionSubtitle>
                        What-if scenarios you've saved against this prediction. The
                        original baseline forecast is preserved separately.
                    </CollectSectionSubtitle>
                </Box>
            </CollectSectionHeader>

            {scenarios.length === 0 ? (
                <CollectEmptyState>
                    <CollectEmptyTitle>No saved scenarios</CollectEmptyTitle>
                    <CollectEmptyHint>
                        Use the what-if simulator above to model a change, then save it
                        here for comparison.
                    </CollectEmptyHint>
                </CollectEmptyState>
            ) : (
                <CollectTableContainer sx={{ mt: 1 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <CollectTableHeadCell>Scenario</CollectTableHeadCell>
                                <CollectTableHeadCell>On-time</CollectTableHeadCell>
                                <CollectTableHeadCell>Risk</CollectTableHeadCell>
                                <CollectTableHeadCell align="right">
                                    Cash impact
                                </CollectTableHeadCell>
                                <CollectTableHeadCell align="right">
                                    Δ vs baseline
                                </CollectTableHeadCell>
                                <CollectTableHeadCell>Saved</CollectTableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {scenarios.map((s, idx) => {
                                const delta = s.cashImpactDelta ?? 0;
                                const deltaColor =
                                    Math.abs(delta) < 1e-9
                                        ? "#6b7280"
                                        : delta > 0
                                            ? "#16a34a"
                                            : "#dc2626";
                                return (
                                    <TableRow key={s._id || idx} hover>
                                        <CollectTableBodyCell>
                                            <Typography sx={{ fontWeight: 600 }}>
                                                {s.name}
                                            </Typography>
                                        </CollectTableBodyCell>
                                        <CollectTableBodyCell>
                                            {Math.round(
                                                (s.figures?.onTimeProbability ?? 0) * 100,
                                            )}
                                            %
                                        </CollectTableBodyCell>
                                        <CollectTableBodyCell>
                                            <Chip
                                                size="small"
                                                label={
                                                    PREDICTION_RISK_LEVEL_LABELS[
                                                    s.figures?.customerRiskLevel ||
                                                    "low"
                                                    ]
                                                }
                                                sx={{
                                                    fontWeight: 700,
                                                    backgroundColor:
                                                        (RISK_COLORS[
                                                            s.figures
                                                                ?.customerRiskLevel ||
                                                            "low"
                                                        ] || "#6b7280") + "1a",
                                                    color:
                                                        RISK_COLORS[
                                                        s.figures
                                                            ?.customerRiskLevel ||
                                                        "low"
                                                        ] || "#6b7280",
                                                }}
                                            />
                                        </CollectTableBodyCell>
                                        <CollectTableBodyCell align="right">
                                            {fmtCurrency(
                                                s.figures?.projectedCashImpact ?? 0,
                                                currency,
                                            )}
                                        </CollectTableBodyCell>
                                        <CollectTableBodyCell align="right">
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    color: deltaColor,
                                                    fontFamily: FONT,
                                                }}
                                            >
                                                {Math.abs(delta) < 1e-9
                                                    ? "—"
                                                    : `${delta > 0 ? "+" : ""}${fmtCurrency(delta, currency)}`}
                                            </Typography>
                                        </CollectTableBodyCell>
                                        <CollectTableBodyCell>
                                            {fmtDate(s.createdAt)}
                                        </CollectTableBodyCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </CollectTableContainer>
            )}
        </CollectSectionCard>
    );
};

export default SavedScenarios;
